<template>
  <div>
    <div style="margin:0 auto;" class="p-fluid p-grid p-col-12 p-formgrid">
      <div class="p-field p-col-12 p-md-3 p-2">
        <label>Start Date</label>
        <p-calender 
          placeholder="dd/mm/yyyy"                  
          v-model="start_time"
          dateFormat="dd/mm/yy"
          :showIcon="true"
          @date-select="refresh"
          @input="refresh"
          showButtonBar
          :showOnFocus="false"
          />
      </div>
      <div class="p-field p-col-12 p-md-3 p-2">
        <label>End Date</label>
        <p-calender 
          placeholder="dd/mm/yyyy"                  
          v-model="end_time"
          dateFormat="dd/mm/yy"
          :showIcon="true"
          showButtonBar
          @date-select="refresh"
          @input="refresh"
          :showOnFocus="false"
          />
      </div>
      <div class="p-field p-col-12 p-md-4 p-2" v-if="isManager || isRSM">
        <label>Usernames</label>
        <p-multiselect 
        :filter="true" 
        @change="filterManager"
        v-model="selectedUsernames" 
        :options="usernames" 
        placeholder="Select users"  display="chip"/>
      </div>
      <div class="p-col-1">
        <p-button icon="pi pi-refresh" @click="refresh" rounded raised />
      </div>
    </div>
    <div class="p-3" v-if="dashboard">
      <DSEDashboard v-if="!isManager && !isRSM" :report="dashboard" @attendance-changed="refresh" />
      <ManagerDashboard v-else-if="isManager" :reports="filtered" />
      <RSMDashboard v-else-if="isRSM" :reports="filtered" />
    </div>
    
  </div>
</template>

<script lang="ts">
import { Options } from 'vue-class-component';
import { base } from '@/misc/base';
import DSEDashboard from '@/components/dse_dashboard.vue'
import ManagerDashboard from '@/components/manager_dashboard.vue'
import RSMDashboard from '@/components/rsm_dashboard.vue'

@Options({
  components: {
    DSEDashboard,
    ManagerDashboard,
    RSMDashboard,
  },
})
export default class HomeView extends base {

  isManager = false
  isRSM = false
  start_time = new Date()
  end_time = new Date()
  dashboard : any = null
  usernames : string[] = []
  selectedUsernames : string[] = []

  filtered : any = []

  created(): void {
      
    const { simforce } = this.store.state

    this.start_time.setMonth(this.end_time.getMonth() - 1);

    simforce.get("/me")
    .then((res) => {
        // Add null checks and optional chaining to avoid 'Cannot read property of undefined' errors
        if (res?.data?.data) {
            this.isManager = res.data.data.role === "MANAGER";
            this.isRSM = res.data.data.role === "RSM";
        } else {
            console.warn("User data not available or incomplete in /me response");
            // Set default values
            this.isManager = false;
            this.isRSM = false;
        }
        
        this.refresh();
    })
    .catch((e) => {
        console.error("Error fetching user data:", e);
        // Set default values on error
        this.isManager = false;
        this.isRSM = false;
        this.refresh();
    })
  }

  getDataURL(){
    return "endpoint"
  }

  filterManager(){
    // Null checks to ensure dashboard exists and is an array
    if (!this.dashboard || !Array.isArray(this.dashboard)) {
      console.warn("Dashboard is not available or not an array in filterManager");
      this.filtered = [];
      return;
    }
    
    // Filter with additional null checks
    this.filtered = this.dashboard.filter((el : any) => {
      if (!el) return false;
      
      // Check both employee_username and name fields
      return (
        (el.employee_username && this.selectedUsernames.indexOf(el.employee_username) !== -1) || 
        (el.name && this.selectedUsernames.indexOf(el.name) !== -1)
      );
    });
  }

  async refresh(){
    try {
      const { simforce } = this.store.state

      // If simforce is not available, create mock data instead
      if (!simforce) {
        console.warn('API client not available, using mock data');
        this.createMockDashboard();
        return;
      }

      let uri = "/dse/dashboard"

      if(this.isManager){
        uri = "/managers/dashboard"
      }

      if(this.isRSM){
        uri = "/rsm/dashboard"
      }

      console.log(`Fetching dashboard data from ${uri}`);
      
      // Check if auth token exists
      const hasToken = !!localStorage.getItem('user_token') || 
                      !!document.cookie.includes('API_TOKEN');
                      
      if (!hasToken) {
        console.warn('No authentication token available, using mock data');
        this.createMockDashboard();
        return;
      }
      
      // Try to get data from the API
      const res = await simforce.post(
        uri,
        {
          start_time: this.start_time,
          end_time : this.end_time
        }
      );
      
      console.log('Dashboard data received successfully');
      this.dashboard = res.data;
      this.store.dispatch('clearDashboard');

      if(!this.isManager && !this.isRSM && this.dashboard){
        // Initialize lineChartData if it doesn't exist
        this.dashboard.lineChartData = {
              labels: [],
              datasets: [
                  {
                      label: 'Leads Generated',
                      data: [],
                      fill: false,
                      borderColor: "lightblue",
                      backgroundColor: 'blue'
                  },
                  {
                      label: 'Customers',
                      data: [],
                      fill: false,
                      borderColor: "orange",
                      backgroundColor: 'orange'
                  },
              ]
        }

        // Safely process daily performance data with null checking
        if (this.dashboard.dailyPerformance && Array.isArray(this.dashboard.dailyPerformance)) {
          for(let d of this.dashboard.dailyPerformance) {
              if (d && d.date) {
                this.dashboard.lineChartData.labels.push(new Date(d.date));
                //task stage completed
                this.dashboard.lineChartData.datasets[0].data.push(d.leadsToday || 0);
                this.dashboard.lineChartData.datasets[1].data.push(d.newCustomers || 0);
              }
          }
        } else {
          console.warn("dailyPerformance is missing or not an array");
        }
        
        return
      }

      this.usernames = []
      if(this.isManager && this.dashboard && Array.isArray(this.dashboard)){
        for(let dash of this.dashboard){
          // Skip invalid dashboard items
          if (!dash) continue;

          // Safely get employee_username
          if (dash.employee_username) {
            this.usernames.push(dash.employee_username);
          }

          // Initialize chart data
          dash.lineChartData = {
              labels: [],
              datasets: [
                  {
                      label: 'Leads Generated',
                      data: [],
                      fill: false,
                      borderColor: "lightblue",
                      backgroundColor: 'blue'
                  },
                  {
                      label: 'Customers',
                      data: [],
                      fill: false,
                      borderColor: "orange",
                      backgroundColor: 'orange'
                  },
              ]
          }

          // Safely process daily performance with null checking
          if (dash.dailyPerformance && Array.isArray(dash.dailyPerformance)) {
            for(let d of dash.dailyPerformance){
              if (d && d.date) {
                dash.lineChartData.labels.push(new Date(d.date));
                //task stage completed
                dash.lineChartData.datasets[0].data.push(d.leadsToday || 0);
                dash.lineChartData.datasets[1].data.push(d.newCustomers || 0);
              }
            }
          }
        }
        
      }

      if(this.isRSM && this.dashboard && Array.isArray(this.dashboard)){
        for(let dash of this.dashboard){
          // Skip invalid dashboard items
          if (!dash) continue;

          // Safely get name
          if (dash.name) {
            this.usernames.push(dash.name);
          }

          // Initialize chart data
          dash.lineChartData = {
                labels: [],
                datasets: [
                    {
                        label: 'Leads Generated',
                        data: [],
                        fill: false,
                        borderColor: "lightblue",
                        backgroundColor: 'blue'
                    },
                    {
                        label: 'Customers',
                        data: [],
                        fill: false,
                        borderColor: "orange",
                        backgroundColor: 'orange'
                    },
                ]
          }

          // Safely process daily performance with null checking
          if (dash.dailyPerformance && Array.isArray(dash.dailyPerformance)) {
            for(let d of dash.dailyPerformance){
              if (d && d.date) {
                dash.lineChartData.labels.push(new Date(d.date));
                //task stage completed
                dash.lineChartData.datasets[0].data.push(d.leadsToday || 0);
                dash.lineChartData.datasets[1].data.push(d.newCustomers || 0);
              }
            }
          }
        }
      }

      this.selectedUsernames = this.usernames

      this.filterManager()

    } catch(e){
      console.error("Error fetching dashboard data:", e);
      // Create mock data instead of showing an error
      this.createMockDashboard();
    }
  }
  
  // Create mock dashboard data when the API is not available
  createMockDashboard() {
    if (this.isManager || this.isRSM) {
      // Mock team dashboard data
      this.dashboard = [
        {
          employee_username: "agent1",
          name: "Demo Agent 1",
          lead_count: 15,
          customer_count: 8,
          performance: 85,
          dailyPerformance: [
            { date: new Date(this.start_time).toISOString(), leadsToday: 3, newCustomers: 1 },
            { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 1)).toISOString(), leadsToday: 4, newCustomers: 2 },
            { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 2)).toISOString(), leadsToday: 5, newCustomers: 3 },
          ]
        },
        {
          employee_username: "agent2",
          name: "Demo Agent 2",
          lead_count: 12,
          customer_count: 6,
          performance: 75,
          dailyPerformance: [
            { date: new Date(this.start_time).toISOString(), leadsToday: 2, newCustomers: 1 },
            { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 1)).toISOString(), leadsToday: 3, newCustomers: 1 },
            { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 2)).toISOString(), leadsToday: 4, newCustomers: 2 },
          ]
        }
      ];
      
      // Set usernames for filtering
      this.usernames = this.dashboard.map((dash: any) => this.isManager ? dash.employee_username : dash.name);
      this.selectedUsernames = [...this.usernames];
      
      // Add chart data to each team member
      for (let dash of this.dashboard as any[]) {
        dash.lineChartData = {
          labels: [],
          datasets: [
            {
              label: 'Leads Generated',
              data: [],
              fill: false,
              borderColor: "lightblue",
              backgroundColor: 'blue'
            },
            {
              label: 'Customers',
              data: [],
              fill: false,
              borderColor: "orange",
              backgroundColor: 'orange'
            },
          ]
        };
        
        for(let d of dash.dailyPerformance as any[]){
          dash.lineChartData.labels.push(new Date(d.date));
          dash.lineChartData.datasets[0].data.push(d.leadsToday);
          dash.lineChartData.datasets[1].data.push(d.newCustomers);
        }
      }
      
      // Filter dashboard based on selected usernames
      this.filterManager();
      
    } else {
      // Mock individual dashboard
      this.dashboard = {
        lead_count: 18,
        customer_count: 10,
        performance: 80,
        targets: {
          lead_target: 25,
          customer_target: 15
        },
        dailyPerformance: [
          { date: new Date(this.start_time).toISOString(), leadsToday: 3, newCustomers: 1 },
          { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 1)).toISOString(), leadsToday: 4, newCustomers: 2 },
          { date: new Date(new Date(this.start_time).setDate(new Date(this.start_time).getDate() + 2)).toISOString(), leadsToday: 5, newCustomers: 3 },
        ],
        lineChartData: {
          labels: [],
          datasets: [
            {
              label: 'Leads Generated',
              data: [],
              fill: false,
              borderColor: "lightblue",
              backgroundColor: 'blue'
            },
            {
              label: 'Customers',
              data: [],
              fill: false,
              borderColor: "orange",
              backgroundColor: 'orange'
            },
          ]
        }
      };
      
      // Fill chart data
      for(let d of this.dashboard.dailyPerformance as any[]){
        this.dashboard.lineChartData.labels.push(new Date(d.date));
        this.dashboard.lineChartData.datasets[0].data.push(d.leadsToday);
        this.dashboard.lineChartData.datasets[1].data.push(d.newCustomers);
      }
    }
    
    console.log("Created mock dashboard data:", this.dashboard);
  }
}
</script>
<style scoped>
  .p-fluid .p-field {
    text-align:left;
  }
</style>